// Teams settings (/settings/teams) — List archetype (UI-LAW §4c): PageHeader
// → teams table; create lives in a Drawer deep-linked via `?new=1`. Browsing
// the list never switches active scope, but creating a team adopts it as the
// working scope and opens its detail settings.
// Team creation is widened to org owner OR org admin (docs/05 §6.2) via the
// membership-derivation seam; this is an affordance gate only — server-side
// authorization remains behind `/v1`.

import { useEffect } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import { parseTeamsSearch, type TeamsSearch } from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useListQuery } from "../lib/useListQuery";
import { type WorkingScopeSearch } from "../lib/workingScope";
import {
  Button,
  EmptyState,
  Page,
  PageHeader,
  RouterLink,
  Stack,
  useToast,
} from "../ui";
import { NameWithId } from "./shared/NameWithId";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";
import { TeamCreateDrawer } from "./teams/TeamCreateDrawer";

type Team = components["schemas"]["TeamResponse"];

// UI-LAW §4 DataTable uniformity: every column is sortable (name + two dates), so
// each carries an accessor. No client facets — teams have no low-cardinality
// column.
function teamColumns(): Array<ColumnDef<Team>> {
  return [
    {
      header: "Team",
      id: "team",
      accessorFn: (team) => team.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Team ID"
          name={
            <RouterLink
              params={{ teamId: row.original.id }}
              to="/settings/teams/$teamId"
            >
              {row.original.display_name}
            </RouterLink>
          }
        />
      ),
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      header: "Updated",
      accessorKey: "updated_at",
      cell: ({ row }) => formatDateTime(row.original.updated_at),
    },
  ];
}

export function TeamsSettings() {
  const navigate = useTypedNavigate<TeamsSearch>();
  // Cross-route navigation that also sets the root-owned working-scope `?team=`.
  const navigateToTeam = useTypedNavigate<WorkingScopeSearch>();
  const queryClient = useQueryClient();
  const toast = useToast();
  // Only the route-owned `new` param is read; `?agent` is ignored here.
  const search = useTypedSearch(parseTeamsSearch);

  // Widened create affordance: org owner or org admin (docs/05 §6.2). Derived
  // from the caller's own memberships + org-owner check, never a `/v1/me` role.
  const authority = useCallerAuthority();

  const isCreateRequested = search.new === 1;
  // While authority is pending/errored the drawer renders nothing and `?new`
  // is retained — stripping before it RESOLVES would kill admin deep-links.
  const isCreateDrawerOpen = isCreateRequested && authority.canManage;
  const authorityResolved = !authority.isPending && !authority.isError;

  useEffect(() => {
    if (isCreateRequested && authorityResolved && !authority.canManage) {
      void navigate({
        replace: true,
        search: mergeSearch<TeamsSearch>({ new: undefined }),
      });
    }
  }, [isCreateRequested, authorityResolved, authority.canManage, navigate]);

  function openCreateDrawer(): void {
    createTeam.reset();
    void navigate({
      search: mergeSearch<TeamsSearch>({ new: 1 }),
    });
  }

  function closeCreateDrawer(): void {
    // Drawer fields reset by unmount; the stale create error resets here.
    createTeam.reset();
    void navigate({
      replace: true,
      search: mergeSearch<TeamsSearch>({ new: undefined }),
    });
  }

  const teams = useListQuery("/v1/teams", {
    queryKey: queryKeys.teams.list(),
  });

  const createTeam = useMutation({
    mutationFn: async (displayName: string) => {
      const { data, error } = await apiClient.POST("/v1/teams", {
        body: { display_name: displayName },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (team) => {
      toast.success("Team created");
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      // Switch the working scope to the new team and open its detail settings
      // (Team ID, rename, delete). The root-owned `?team=` makes the shell
      // selector follow; leaving the list route drops `?new=1` and the drawer.
      void navigateToTeam({
        params: { teamId: team.id },
        search: { team: team.id },
        to: "/settings/teams/$teamId",
      });
    },
    onError: (error) => {
      toast.error("Team create failed", {
        description: requestErrorDescription(error, "Team request failed."),
      });
    },
  });

  return (
    <TeamsSettingsView
      canCreate={authority.canManage}
      createErrorMessage={
        createTeam.isError ? createTeam.error.message : undefined
      }
      hasNextPage={teams.hasNextPage}
      isCreateDrawerOpen={isCreateDrawerOpen}
      isCreatePending={createTeam.isPending}
      isFetchingNextPage={teams.isFetchingNextPage}
      isPending={teams.isPending}
      items={teams.items}
      listErrorMessage={teams.errorMessage}
      listStateKey={teams.listStateKey}
      onCloseCreate={closeCreateDrawer}
      onLoadMore={teams.fetchNextPage}
      onOpenCreate={openCreateDrawer}
      onSubmitCreate={(displayName) => {
        createTeam.mutate(displayName);
      }}
    />
  );
}

export interface TeamsSettingsViewProps {
  canCreate: boolean;
  createErrorMessage?: string;
  hasNextPage: boolean;
  isCreateDrawerOpen: boolean;
  isCreatePending: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: Team[];
  listErrorMessage?: string;
  listStateKey: string;
  onCloseCreate: () => void;
  onLoadMore: () => void;
  onOpenCreate: () => void;
  onSubmitCreate: (displayName: string) => void;
}

export function TeamsSettingsView({
  canCreate,
  createErrorMessage,
  hasNextPage,
  isCreateDrawerOpen,
  isCreatePending,
  isFetchingNextPage,
  isPending,
  items,
  listErrorMessage,
  listStateKey,
  onCloseCreate,
  onLoadMore,
  onOpenCreate,
  onSubmitCreate,
}: TeamsSettingsViewProps) {
  return (
    <Page variant="config">
      <Stack gap={6}>
        <PageHeader
          actions={
            canCreate ? (
              <Button size="sm" type="button" onClick={onOpenCreate}>
                Create team
              </Button>
            ) : undefined
          }
        />
        <PaginatedTableSection
          ariaLabel="Teams"
          columns={teamColumns()}
          emptyState={
            <EmptyState
              action={
                canCreate ? (
                  <Button type="button" onClick={onOpenCreate}>
                    Create team
                  </Button>
                ) : undefined
              }
              title="No teams"
            />
          }
          enableSorting
          errorMessage={listErrorMessage}
          getRowId={(team) => team.id}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isPending={isPending}
          items={items}
          listStateKey={listStateKey}
          skeletonLabel="Loading teams"
          skeletonRows={3}
          onLoadMore={onLoadMore}
        />
      </Stack>
      {isCreateDrawerOpen ? (
        <TeamCreateDrawer
          createErrorMessage={createErrorMessage}
          isCreatePending={isCreatePending}
          onDismiss={onCloseCreate}
          onSubmit={onSubmitCreate}
        />
      ) : null}
    </Page>
  );
}
